/**
 * DebugPanel - Collapsible bottom panel with two tabs:
 *   1. Pipeline — structured SSE debug_log events (real-time)
 *   2. Server Log — full raw backend journalctl output
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import {
  Close as CloseIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DebugLogEntry, fetchServerLogs } from '../../services/v3RagService';

type TabId = 'pipeline' | 'server';

interface DebugPanelProps {
  logs: DebugLogEntry[];
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

// ─── Colors for log line highlighting ───
const LOG_COLORS: Record<string, string> = {
  'SARAH STREAM': '#4FC3F7',
  'STEP': '#81C784',
  'SEARCH': '#90CAF9',
  'ROUTE': '#AED581',
  'SELECT': '#FFF176',
  'READ': '#80CBC4',
  'PARALLEL': '#CE93D8',
  'PLANNER': '#CE93D8',
  'COST': '#F48FB1',
  'DONE': '#B39DDB',
  'ERROR': '#EF5350',
  'WARNING': '#FFA726',
};

// Ordered list for filter chips
const LOG_FILTER_KEYS = [
  'SARAH STREAM', 'STEP', 'SEARCH', 'ROUTE', 'SELECT', 'READ',
  'PARALLEL', 'PLANNER', 'COST', 'DONE', 'ERROR', 'WARNING',
];

const getLineCategory = (line: string): string | null => {
  for (const keyword of LOG_FILTER_KEYS) {
    if (line.includes(keyword)) return keyword;
  }
  return null;
};

const colorizeLogLine = (line: string): React.ReactNode => {
  // Find the first matching keyword to determine color
  for (const [keyword, color] of Object.entries(LOG_COLORS)) {
    if (line.includes(keyword)) {
      return <span style={{ color }}>{line}</span>;
    }
  }
  // HTTP requests — dim
  if (line.includes('HTTP Request:') || line.includes('HTTP/2')) {
    return <span style={{ color: '#555' }}>{line}</span>;
  }
  // Default
  return <span style={{ color: '#b0b0b0' }}>{line}</span>;
};

// ─── Pipeline tab constants ───
const PHASE_COLORS: Record<string, string> = {
  understand: '#1565C0',
  route: '#2E7D32',
  blocks: '#E65100',
  done: '#7B1FA2',
};

const PHASE_LABELS: Record<string, string> = {
  understand: 'UNDERSTAND',
  route: 'ROUTE',
  blocks: 'BLOCKS',
  done: 'DONE',
};

const formatMs = (ms: number) => {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

// ─── Pipeline LogEntry component ───
const LogEntry: React.FC<{ entry: DebugLogEntry }> = ({ entry }) => {
  const color = PHASE_COLORS[entry.phase] || '#555';
  const label = PHASE_LABELS[entry.phase] || entry.phase.toUpperCase();

  return (
    <Box sx={{ mb: 1, fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        <Chip
          label={label}
          size="small"
          sx={{ bgcolor: color, color: 'white', fontSize: '0.65rem', height: 18, fontWeight: 700, letterSpacing: 0.5 }}
        />
        {entry.elapsed_ms != null && (
          <Typography sx={{ fontSize: '0.72rem', color: '#888', fontFamily: 'monospace' }}>
            {formatMs(entry.elapsed_ms || entry.total_ms || 0)}
          </Typography>
        )}
        {entry.total_ms != null && (
          <Typography sx={{ fontSize: '0.72rem', color: '#888', fontFamily: 'monospace' }}>
            total: {formatMs(entry.total_ms)}
          </Typography>
        )}
      </Box>

      {entry.phase === 'understand' && (
        <Box sx={{ pl: 1, color: '#ccc' }}>
          {entry.tickers?.length > 0 && <div>Tickers: <b style={{ color: '#4FC3F7' }}>{entry.tickers.join(', ')}</b></div>}
          {entry.companies?.length > 0 && <div>Companies: {entry.companies.join(', ')}</div>}
          {entry.sectors?.length > 0 && <div>Sectors: {entry.sectors.join(', ')}</div>}
          {entry.metrics?.length > 0 && <div>Metrics: {entry.metrics.join(', ')}</div>}
          <div>Follow-up: <span style={{ color: entry.follow_up_type === 'topic_shift' ? '#EF5350' : '#81C784' }}>{entry.follow_up_type}</span></div>
        </Box>
      )}

      {entry.phase === 'route' && (
        <Box sx={{ pl: 1, color: '#ccc' }}>
          <div>Gateway: <b style={{ color }}>{entry.gateway}</b> | {entry.docs_found} docs | {entry.blocks_total} blocks</div>
          {entry.documents?.map((doc: any, i: number) => (
            <Box key={i} sx={{ ml: 1, mt: 0.25, display: 'flex', gap: 0.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <span style={{
                color: doc.confidence >= 0.9 ? '#81C784' : doc.confidence >= 0.7 ? '#FFB74D' : '#EF5350',
                fontWeight: 600, minWidth: 36,
              }}>
                {(doc.confidence * 100).toFixed(0)}%
              </span>
              <span style={{ flex: 1, color: '#e0e0e0' }}>{doc.title}</span>
              <span style={{ color: '#777', fontSize: '0.7rem' }}>{doc.reason}</span>
              {doc.topics?.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                  {doc.topics.slice(0, 3).map((t: string, j: number) => (
                    <Chip key={j} label={t} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(129,199,132,0.15)', color: '#81C784' }} />
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {entry.phase === 'blocks' && (
        <Box sx={{ pl: 1, color: '#ccc' }}>
          <div>Selected: <b>{entry.blocks_selected}</b> blocks | {entry.citations_mapped} citations | {entry.doc_extractions} doc extractions</div>
          {entry.block_types && (
            <div>Types: {Object.entries(entry.block_types).map(([k, v]) => `${v} ${k}`).join(', ')}</div>
          )}
          {entry.mini_reports > 0 && <div>Sub-agent reports: {entry.mini_reports}</div>}
          {entry.themes && <div style={{ color: '#999', fontStyle: 'italic' }}>Themes: {entry.themes}</div>}
        </Box>
      )}

      {entry.phase === 'done' && (
        <Box sx={{ pl: 1, color: '#ccc' }}>
          <div>Total: <b style={{ color: '#B39DDB' }}>{formatMs(entry.total_ms)}</b> | Answer: {entry.answer_length} chars | Sources: {entry.sources_count}</div>
          {entry.cost && (
            <div>Cost: <b style={{ color: '#F48FB1' }}>${entry.cost.total_cost?.toFixed(4)}</b> | Tokens: {entry.cost.total_input_tokens}in/{entry.cost.total_output_tokens}out | DB: {entry.cost.db_queries} queries</div>
          )}
        </Box>
      )}
    </Box>
  );
};

// ─── Main DebugPanel ───
const DebugPanel: React.FC<DebugPanelProps> = ({ logs, isOpen, onToggle, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabId>('pipeline');
  const [serverLines, setServerLines] = useState<string[]>([]);
  const [serverLoading, setServerLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const pipelineRef = useRef<HTMLDivElement>(null);
  const serverRef = useRef<HTMLDivElement>(null);

  // Auto-scroll pipeline to bottom
  useEffect(() => {
    if (pipelineRef.current && isOpen && activeTab === 'pipeline') {
      pipelineRef.current.scrollTop = pipelineRef.current.scrollHeight;
    }
  }, [logs, isOpen, activeTab]);

  // Auto-scroll server log to bottom
  useEffect(() => {
    if (serverRef.current && isOpen && activeTab === 'server') {
      serverRef.current.scrollTop = serverRef.current.scrollHeight;
    }
  }, [serverLines, isOpen, activeTab]);

  // Auto-fetch server logs when switching to server tab
  const loadServerLogs = useCallback(async () => {
    setServerLoading(true);
    const lines = await fetchServerLogs(400);
    setServerLines(lines);
    setServerLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'server' && isOpen && serverLines.length === 0) {
      loadServerLogs();
    }
  }, [activeTab, isOpen, serverLines.length, loadServerLogs]);

  // Auto-refresh server logs when pipeline emits "done" phase or when tab is opened
  const lastLogCount = useRef(logs.length);
  useEffect(() => {
    if (logs.length > lastLogCount.current) {
      const newEntries = logs.slice(lastLogCount.current);
      lastLogCount.current = logs.length;
      // Auto-refresh when "done" arrives, or on first log entry
      if (newEntries.some(e => e.phase === 'done') || logs.length === 1) {
        loadServerLogs();
      }
    }
  }, [logs, loadServerLogs]);

  const toggleFilter = useCallback((keyword: string) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(keyword)) {
        next.delete(keyword);
      } else {
        next.add(keyword);
      }
      return next;
    });
  }, []);

  // Scope server lines to the most recent query session
  const sessionScopedLines = useMemo(() => {
    if (!serverLines.length) return serverLines;
    // Find the last "SARAH STREAM] START" line — that marks beginning of latest query
    let startIdx = 0;
    for (let i = serverLines.length - 1; i >= 0; i--) {
      if (serverLines[i].includes('SARAH STREAM') && serverLines[i].includes('START')) {
        startIdx = i;
        break;
      }
    }
    return serverLines.slice(startIdx);
  }, [serverLines]);

  const filteredServerLines = useMemo(() => {
    if (activeFilters.size === 0) return sessionScopedLines;
    return sessionScopedLines.filter(line => {
      const cat = getLineCategory(line);
      return cat !== null && activeFilters.has(cat);
    });
  }, [sessionScopedLines, activeFilters]);

  if (!isOpen && logs.length === 0) return null;

  const tabStyle = (tab: TabId) => ({
    px: 1.5,
    py: 0.5,
    fontSize: '0.72rem',
    fontWeight: activeTab === tab ? 700 : 400,
    color: activeTab === tab ? '#4FC3F7' : '#94a3b8',
    borderBottom: activeTab === tab ? '2px solid #4FC3F7' : '2px solid transparent',
    cursor: 'pointer',
    fontFamily: 'monospace',
    letterSpacing: 0.5,
    transition: 'all 0.2s',
    '&:hover': { color: '#4FC3F7' },
  });

  return (
    <Box sx={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1300,
      bgcolor: '#1a1a2e',
      color: '#e0e0e0',
      borderTop: '2px solid #4A90E2',
      transition: 'height 0.3s ease',
      height: isOpen ? 320 : 32,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header bar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        px: 1,
        bgcolor: '#16213e',
        minHeight: 32,
        gap: 0,
      }}>
        {/* Clickable title to toggle */}
        <Box onClick={onToggle} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#4A90E2', fontFamily: 'monospace', letterSpacing: 1 }}>
            DEBUG
          </Typography>
          {logs.length > 0 && (
            <Chip label={`${logs.length}`} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#0f3460', color: '#94a3b8' }} />
          )}
        </Box>

        {/* Tab navigator */}
        {isOpen && (
          <Box sx={{ display: 'flex', gap: 0 }}>
            <Box sx={tabStyle('pipeline')} onClick={() => setActiveTab('pipeline')}>
              PIPELINE
            </Box>
            <Box sx={tabStyle('server')} onClick={() => setActiveTab('server')}>
              SERVER LOG
            </Box>
          </Box>
        )}

        {/* Collapsed status */}
        {!isOpen && logs.length > 0 && (
          <Typography onClick={onToggle} sx={{ fontSize: '0.7rem', color: '#94a3b8', fontFamily: 'monospace', cursor: 'pointer' }}>
            {PHASE_LABELS[logs[logs.length - 1].phase] || logs[logs.length - 1].phase}
            {logs[logs.length - 1].total_ms ? ` - ${formatMs(logs[logs.length - 1].total_ms)}` : ''}
          </Typography>
        )}

        <Box sx={{ ml: 'auto', display: 'flex' }}>
          {/* Refresh button for server log tab */}
          {isOpen && activeTab === 'server' && (
            <IconButton size="small" onClick={loadServerLogs} disabled={serverLoading} sx={{ color: '#94a3b8' }}>
              <RefreshIcon fontSize="small" sx={{ animation: serverLoading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
            </IconButton>
          )}
          <IconButton size="small" onClick={onToggle} sx={{ color: '#94a3b8' }}>
            {isOpen ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClose(); }} sx={{ color: '#94a3b8' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content area */}
      {isOpen && (
        <>
          {/* Pipeline tab */}
          {activeTab === 'pipeline' && (
            <Box
              ref={pipelineRef}
              sx={{
                flex: 1,
                overflow: 'auto',
                px: 1.5,
                py: 1,
                fontFamily: 'monospace',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': { bgcolor: '#333', borderRadius: 3 },
              }}
            >
              {logs.length === 0 ? (
                <Typography sx={{ color: '#555', fontSize: '0.75rem', fontStyle: 'italic' }}>
                  Waiting for query... Pipeline events will appear here.
                </Typography>
              ) : (
                logs.map((entry, i) => <LogEntry key={i} entry={entry} />)
              )}
            </Box>
          )}

          {/* Server Log tab */}
          {activeTab === 'server' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              {/* Filter chips */}
              <Box sx={{
                display: 'flex',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                flexWrap: 'wrap',
                bgcolor: '#16213e',
                borderBottom: '1px solid #2a2a4a',
                flexShrink: 0,
              }}>
                <Chip
                  label="ALL"
                  size="small"
                  onClick={() => setActiveFilters(new Set())}
                  sx={{
                    height: 20, fontSize: '0.6rem', fontWeight: 700, fontFamily: 'monospace',
                    bgcolor: activeFilters.size === 0 ? '#4A90E2' : '#2a2a4a',
                    color: activeFilters.size === 0 ? '#fff' : '#94a3b8',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.85 },
                  }}
                />
                {LOG_FILTER_KEYS.map(keyword => (
                  <Chip
                    key={keyword}
                    label={keyword}
                    size="small"
                    onClick={() => toggleFilter(keyword)}
                    sx={{
                      height: 20, fontSize: '0.58rem', fontWeight: 600, fontFamily: 'monospace',
                      bgcolor: activeFilters.has(keyword) ? LOG_COLORS[keyword] : '#2a2a4a',
                      color: activeFilters.has(keyword) ? '#1a1a2e' : LOG_COLORS[keyword],
                      border: `1px solid ${activeFilters.has(keyword) ? LOG_COLORS[keyword] : 'transparent'}`,
                      cursor: 'pointer',
                      opacity: activeFilters.size > 0 && !activeFilters.has(keyword) ? 0.5 : 1,
                      '&:hover': { opacity: 0.85, borderColor: LOG_COLORS[keyword] },
                    }}
                  />
                ))}
              </Box>
              {/* Log content */}
              <Box
                ref={serverRef}
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontFamily: 'monospace',
                  fontSize: '0.72rem',
                  lineHeight: 1.45,
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: '#333', borderRadius: 3 },
                }}
              >
                {serverLoading && serverLines.length === 0 ? (
                  <Typography sx={{ color: '#555', fontSize: '0.75rem', fontStyle: 'italic' }}>
                    Loading server logs...
                  </Typography>
                ) : (
                  filteredServerLines.map((line, i) => (
                    <div key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {colorizeLogLine(line)}
                    </div>
                  ))
                )}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default DebugPanel;
